/*
  AXORY US storefront behaviour: Year/Make/Model finder, tabs, product gallery,
  quantity stepper, "fits your vehicle" check and the makes toggle.
  Loaded only for the United States market (layout/theme.liquid).
*/
(() => {
  if (window.axusLoaded) return;
  window.axusLoaded = true;

  const STORE_KEY = 'axus:vehicle';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let dataPromise;

  const loadData = (src) => {
    dataPromise =
      dataPromise ||
      fetch(src).then((r) => {
        if (!r.ok) throw new Error(`fitment data ${r.status}`);
        return r.json();
      });
    return dataPromise;
  };

  const readVehicle = () => {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || null;
    } catch (e) {
      return null;
    }
  };

  const saveVehicle = (v) => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(v));
    } catch (e) {
      /* storage unavailable: finder still works for this page view */
    }
  };

  const esc = (s) =>
    String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const years = (y1, y2) => (y1 === y2 ? `${y1}` : `${y1}–${y2}`);
  const productUrl = (handle) => `${root}products/${handle}`;
  const byText = (a, b) => a.localeCompare(b, 'en', { numeric: true, sensitivity: 'base' });

  /* ---------------- Year / Make / Model ---------------- */
  class AxusYmm extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.year = this.form.elements.year;
      this.make = this.form.elements.make;
      this.model = this.form.elements.model;
      this.button = this.form.querySelector('[type="submit"]');
      this.results = this.querySelector('[data-results]');

      const warm = () => this.ready().catch(() => {});
      ['pointerdown', 'focusin', 'touchstart'].forEach((type) =>
        this.addEventListener(type, warm, { once: true, passive: true }),
      );
      const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
      if (document.readyState === 'complete') idle(warm);
      else window.addEventListener('load', () => idle(warm), { once: true });

      this.year.addEventListener('change', () => this.onYear());
      this.make.addEventListener('change', () => this.onMake());
      this.model.addEventListener('change', () => {
        this.sync();
        if (this.model.value) this.show();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.show();
      });

      const saved = readVehicle();
      if (saved) this.ready().then(() => this.restore(saved)).catch(() => {});
    }

    ready() {
      this.readyPromise =
        this.readyPromise ||
        loadData(this.dataset.src)
          .then((data) => {
            this.data = data;
            this.fillYears();
            return data;
          })
          .catch((error) => {
            this.fail();
            throw error;
          });
      return this.readyPromise;
    }

    fillYears() {
      const current = this.year.value;
      let min = Infinity;
      let max = -Infinity;
      this.data.fits.forEach((f) => {
        min = Math.min(min, f[3]);
        max = Math.max(max, f[4]);
      });
      const list = [];
      for (let y = max; y >= min; y -= 1) {
        if (this.data.fits.some((f) => f[3] <= y && y <= f[4])) list.push(String(y));
      }
      this.fill(this.year, list, this.year.options[0].textContent);
      if (list.includes(current)) this.year.value = current;
    }

    fits(year, make) {
      return this.data.fits.filter(
        (f) => f[3] <= year && year <= f[4] && (make === undefined || this.data.makes[f[0]] === make),
      );
    }

    fill(select, values, placeholder) {
      select.length = 1;
      select.options[0].textContent = placeholder;
      values.forEach((v) => select.add(new Option(v, v)));
      select.disabled = values.length === 0;
    }

    async onYear() {
      await this.ready();
      const year = Number(this.year.value);
      const makes = year ? [...new Set(this.fits(year).map((f) => this.data.makes[f[0]]))].sort(byText) : [];
      this.fill(this.make, makes, 'Select make');
      this.fill(this.model, [], 'Select model');
      this.sync();
    }

    onMake() {
      const year = Number(this.year.value);
      const make = this.make.value;
      const models = make ? [...new Set(this.fits(year, make).map((f) => f[1]))].sort(byText) : [];
      this.fill(this.model, models, 'Select model');
      this.sync();
    }

    sync() {
      this.button.disabled = !(this.year.value && this.make.value && this.model.value);
      this.results.hidden = true;
    }

    restore(v) {
      if (![...this.year.options].some((o) => o.value === String(v.year))) return;
      this.year.value = String(v.year);
      const makes = [...new Set(this.fits(v.year).map((f) => this.data.makes[f[0]]))].sort(byText);
      this.fill(this.make, makes, 'Select make');
      if (!makes.includes(v.make)) return this.sync();
      this.make.value = v.make;
      this.onMake();
      if ([...this.model.options].some((o) => o.value === v.model)) this.model.value = v.model;
      this.sync();
    }

    async show() {
      try {
        await this.ready();
      } catch (e) {
        return;
      }
      const year = Number(this.year.value);
      const make = this.make.value;
      const model = this.model.value;
      if (!(year && make && model)) return;

      const rows = this.fits(year, make).filter((f) => f[1] === model);
      saveVehicle({ year, make, model });
      document.dispatchEvent(new CustomEvent('axus:vehicle', { detail: { year, make, model } }));

      this.results.hidden = false;
      if (!rows.length) {
        this.results.innerHTML = `<p class="axus-ymm__msg">We don't list a fit for the ${esc(year)} ${esc(make)} ${esc(model)} yet. <a href="${root}collections/roof-rack-cross-bars">Browse all roof bars</a> or contact us with your vehicle details.</p>`;
        return;
      }

      const label = rows.length === 1 ? '1 fit' : `${rows.length} fits`;
      this.results.innerHTML =
        `<p class="axus-ymm__count">${label} for your ${esc(year)} ${esc(make)} ${esc(model)}${rows.length > 1 ? ' — pick your generation' : ''}</p>` +
        rows.map((r) => this.card(r, make)).join('');
      this.prices();
    }

    card(r, make) {
      const rail = this.data.lines[r[5]] || '';
      const chip = (handle, cls, name) =>
        handle
          ? `<a class="axus-chip" href="${productUrl(handle)}"><span class="axus-dot axus-dot--${cls}" aria-hidden="true"></span>${name}</a>`
          : '';
      return `<article class="axus-fit">
        <div>
          <span class="axus-fit__tag">${esc(rail)}</span>
          <h3 class="axus-fit__name">${esc(make)} ${esc(r[2])}</h3>
          <p class="axus-fit__meta">Model years ${years(r[3], r[4])} · for ${esc(rail.toLowerCase().replace(' rail', ''))} roof rails · set of 2 lockable aluminum bars</p>
        </div>
        <div class="axus-fit__buy">
          <span class="axus-fit__price" data-handle="${esc(r[6] || r[7])}"></span>
          ${chip(r[6], 'black', 'Black')}${chip(r[7], 'silver', 'Silver')}
        </div>
      </article>`;
    }

    prices() {
      this.results.querySelectorAll('[data-handle]').forEach((el) => {
        productData(el.dataset.handle).then((p) => {
          const v = p && p.variants[0];
          if (!v) return el.remove();
          if (!p.available) {
            el.textContent = 'Sold out';
            return;
          }
          el.innerHTML = p.discount_percent
            ? `<span class="axus-p__now">${esc(v.final_price_formatted)}</span> <s class="axus-p__was">${esc(v.price_formatted)}</s>`
            : esc(v.price_formatted);
        });
      });
    }

    fail() {
      this.results.hidden = false;
      this.results.innerHTML = `<p class="axus-ymm__msg">The vehicle finder didn't load. <a href="${root}collections/roof-rack-cross-bars">Browse all roof bars</a> instead.</p>`;
    }
  }

  if (!customElements.get('axus-ymm')) customElements.define('axus-ymm', AxusYmm);

  /* ---------------- Live product data (price, discount, stock) ---------------- */
  const productCache = {};
  const productData = (handle) => {
    productCache[handle] =
      productCache[handle] ||
      fetch(`${productUrl(handle)}?view=axus-json`)
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);
    return productCache[handle];
  };

  /* ---------------- Registration lookup (UK) ---------------- */
  const ROOF = {
    raised: { t: 'Raised roof rails', d: 'The rails stand off the roof: you can pass a hand underneath.' },
    flush: { t: 'Flush roof rails', d: 'The rails sit tight against the roof with no gap underneath.' },
    fix: { t: 'Factory fixing points', d: 'No rails, but small covered fixing points along the roof edge.' },
  };
  const ROOF_ORDER = ['raised', 'flush', 'fix'];

  class AxusReg extends HTMLElement {
    connectedCallback() {
      if (this.form) return;
      this.form = this.querySelector('form');
      this.input = this.form.elements.reg;
      this.status = this.querySelector('[data-status]');
      this.api = (this.dataset.api || '').replace(/\/$/, '');
      this.email = this.dataset.email;
      this.input.addEventListener('input', () => {
        this.input.value = this.input.value.toUpperCase().replace(/[^A-Z0-9 ]/g, '').replace(/\s+/g, ' ').slice(0, 9);
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.lookup();
      });
    }

    get stage() {
      return document.getElementById(this.dataset.results);
    }

    lookup() {
      const plate = this.input.value.trim();
      const reg = plate.replace(/\s/g, '');
      if (reg.length < 2) {
        this.input.focus();
        return;
      }
      this.classList.add('is-loading');
      this.status.textContent = 'Checking the DVLA record…';
      fetch(`${this.api}/api/vrm?reg=${encodeURIComponent(reg)}`)
        .then((r) => r.json())
        .then((data) => {
          if (!data || data.ok !== true) throw new Error('bad response');
          this.status.textContent = '';
          try {
            sessionStorage.setItem('aax_vrm', JSON.stringify({ reg, at: Date.now() }));
          } catch (e) {
            /* ignore */
          }
          this.render(data, plate);
        })
        .catch(() => {
          this.status.textContent = `Something went wrong on our side. Please try again, or email ${this.email}.`;
        })
        .finally(() => this.classList.remove('is-loading'));
    }

    mailto(subject) {
      return `mailto:${this.email}?subject=${encodeURIComponent(subject)}`;
    }

    render(data, plate) {
      const stage = this.stage;
      if (!stage) return;
      stage.hidden = false;
      stage.innerHTML = '';
      const all = this.dataset.all;

      if (data.status === 'none' || !data.vehicle) {
        stage.innerHTML = `<div class="axus-regv axus-regv--none">
            <span class="axus-regv__plate">${esc(plate)}</span>
            <div><h3>We couldn't match that plate</h3><p>Nothing in our range is listed for this vehicle yet.</p></div>
          </div>
          <p class="axus-reg__msg">Browse <a href="${all}">all roof bars</a>, or email <a href="${this.mailto(`Roof bar check — ${plate}`)}">${esc(this.email)}</a> with your reg and we'll check by hand.</p>`;
        stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      const v = data.vehicle;
      const live = (data.groups || []).some((g) => g.live);
      const tidy = (x) =>
        String(x)
          .split(/\s+/)
          .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
          .join(' ');
      const facts = [v.year, v.body, v.fuel, v.colour]
        .filter(Boolean)
        .map(tidy)
        .map((x) => `<span>${esc(x)}</span>`)
        .join('');
      const pill =
        data.status === 'choose'
          ? '<span class="axus-pill axus-pill--warn">One question left</span>'
          : live
            ? '<span class="axus-pill axus-pill--ok">Bars available</span>'
            : '<span class="axus-pill axus-pill--warn">Fits, out of stock</span>';
      stage.insertAdjacentHTML(
        'beforeend',
        `<div class="axus-regv"><span class="axus-regv__plate">${esc(plate)}</span>
          <div><h3>${esc(v.label)}</h3><div class="axus-regv__facts">${facts}</div></div>${pill}</div>`,
      );

      if (data.notice === 'plate_in_retention') {
        stage.insertAdjacentHTML(
          'beforeend',
          `<p class="axus-reg__msg axus-reg__msg--warn"><strong>Double-check this one.</strong> This registration has been transferred, so the DVLA record may describe the plate's previous car. If the vehicle above isn't yours, browse <a href="${all}">all roof bars</a> or email <a href="${this.mailto(`Roof bar check — ${plate}`)}">${esc(this.email)}</a>.</p>`,
        );
      }

      if (data.status === 'choose') {
        stage.appendChild(this.chooser(data, plate));
      } else {
        this.paint(data.groups || []);
      }
      stage.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    chooser(data, plate) {
      const box = document.createElement('div');
      box.className = 'axus-regq';
      box.innerHTML =
        '<h3>What does your roof look like?</h3><p>This model was built with more than one roof type and the bars are not interchangeable. Look along the top of your car and pick what you see.</p>';
      const byRoof = {};
      data.groups.forEach((g) => {
        (byRoof[g.roofType] = byRoof[g.roofType] || []).push(g);
      });
      const opts = document.createElement('div');
      opts.className = 'axus-regq__opts';
      ROOF_ORDER.forEach((rt) => {
        if (!byRoof[rt]) return;
        const img = this.dataset[`img${rt.charAt(0).toUpperCase()}${rt.slice(1)}`];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'axus-regq__opt';
        b.innerHTML = `${img ? `<img src="${img}" alt="" loading="lazy">` : ''}<strong>${ROOF[rt].t}</strong><span>${ROOF[rt].d}</span>`;
        b.addEventListener('click', () => {
          opts.querySelectorAll('button').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          this.stage.querySelectorAll('.axus-regg, .axus-regs').forEach((n) => n.remove());
          this.paint(byRoof[rt]);
        });
        opts.appendChild(b);
      });
      box.appendChild(opts);
      box.insertAdjacentHTML(
        'beforeend',
        `<p class="axus-reg__msg">None of these match your roof? <a href="${this.mailto(`Roof check — ${plate}`)}">Send us a photo</a> and we'll tell you which bars fit.</p>`,
      );
      return box;
    }

    paint(groups) {
      const stage = this.stage;
      const anyLive = groups.some((g) => g.live);
      stage.insertAdjacentHTML(
        'beforeend',
        `<div class="axus-regs"><h3>${anyLive ? 'Roof bars that fit' : "These fit, but we're out of stock"}</h3><p>${
          anyLive ? 'Pick the set that matches your roof.' : "Email us and we'll tell you the day they're back."
        }</p></div>`,
      );
      groups.forEach((g, i) => {
        const box = document.createElement('section');
        box.className = `axus-regg${i === 0 && g.live ? ' is-best' : ''}${g.live ? '' : ' is-soon'}`;
        box.innerHTML = `<div class="axus-regg__head"><span class="axus-regg__sys">${esc(g.name)}</span>
            <div><strong>${esc(g.roof)}</strong><span>${esc(g.blurb || '')}</span></div>
            <span class="axus-regg__count">${esc(g.live ? (g.vehicles || []).join(' · ') : 'Restocking')}</span></div>
          <div class="axus-regg__cards"></div>`;
        const cards = box.querySelector('.axus-regg__cards');
        (g.offers || []).forEach((o) => cards.appendChild(this.offer(o, g)));
        stage.appendChild(box);
      });
    }

    offer(o, g) {
      const card = document.createElement('div');
      card.className = 'axus-rego';
      const name = `${esc(g.name)} roof bars, ${esc(o.colour || '')}${o.length ? `, ${esc(o.length)}` : ''}`;
      if (!o.listed) {
        card.classList.add('is-soon');
        card.innerHTML = `<div class="axus-rego__img axus-rego__img--empty">Not listed yet</div>
          <p class="axus-rego__name">${name}</p><p class="axus-rego__sku">${esc(o.sku)}</p>
          <a class="axus-btn axus-btn--ghost" href="${this.mailto(`Roof bar enquiry — ${o.sku}`)}">Email me when it's back</a>`;
        return card;
      }
      card.innerHTML = `<a class="axus-rego__link" href="${productUrl(o.handle)}">
          <span class="axus-rego__img">${o.image ? `<img src="${esc(o.image)}" alt="${name}" loading="lazy">` : ''}</span>
          <span class="axus-rego__name">${name}</span></a>
        <p class="axus-rego__sku">${esc(o.sku)}</p>
        <div class="axus-rego__foot"><span class="axus-rego__price" data-price></span><span class="axus-rego__stock" data-stock></span></div>
        <button type="button" class="axus-btn axus-btn--red" data-add disabled>Add to basket</button>`;
      productData(o.handle).then((p) => {
        const button = card.querySelector('[data-add]');
        const price = card.querySelector('[data-price]');
        const stock = card.querySelector('[data-stock]');
        const variant = p && (p.variants.find((x) => x.sku === o.sku) || (p.variants.length === 1 ? p.variants[0] : null));
        if (!variant) {
          button.outerHTML = `<a class="axus-btn axus-btn--ghost" href="${productUrl(o.handle)}">View product</a>`;
          return;
        }
        card.querySelector('.axus-rego__link').href = `${productUrl(o.handle)}?variant=${variant.id}`;
        if (p.image) {
          card.querySelector('.axus-rego__img').innerHTML = `<img src="${esc(p.image)}" alt="${name}" loading="lazy">`;
        }
        price.innerHTML = p.discount_percent
          ? `<span class="axus-p__now">${esc(variant.final_price_formatted)}</span> <s class="axus-p__was">${esc(variant.price_formatted)}</s> <span class="axus-p__off">${p.discount_percent}% off</span>`
          : `<span class="axus-p__now">${esc(variant.price_formatted)}</span>`;
        if (!variant.available) {
          stock.textContent = 'Back-order';
          stock.className = 'axus-rego__stock is-out';
          button.outerHTML = `<a class="axus-btn axus-btn--ghost" href="${productUrl(o.handle)}?variant=${variant.id}">View product</a>`;
          return;
        }
        stock.textContent = 'In stock';
        button.disabled = false;
        button.addEventListener('click', () => {
          button.disabled = true;
          button.textContent = 'Adding…';
          fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
          })
            .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
            .then(() => {
              button.textContent = 'Added ✓';
              window.location.href = `${root}cart`;
            })
            .catch(() => {
              button.disabled = false;
              button.textContent = 'Add to basket';
            });
        });
      });
      return card;
    }
  }

  if (!customElements.get('axus-reg')) customElements.define('axus-reg', AxusReg);

  /* ---------------- Random product rows ---------------- */
  document.querySelectorAll('[data-axus-random]').forEach((row) => {
    const keep = Number(row.dataset.axusRandom) || 5;
    const cards = [...row.children];
    for (let i = cards.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    cards.forEach((card, i) => {
      if (i < keep) row.appendChild(card);
      else card.remove();
    });
  });

  /* ---------------- Tabs ---------------- */
  document.querySelectorAll('[data-axus-tabs]').forEach((list) => {
    const tabs = [...list.querySelectorAll('[role="tab"]')];
    const activate = (tab, focus) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
      if (focus) tab.focus();
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => activate(tab));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        activate(tabs[(i + step + tabs.length) % tabs.length], true);
      });
    });
  });

  /* ---------------- Product gallery ---------------- */
  document.querySelectorAll('[data-axus-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-track]');
    const slides = [...track.children];
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const count = gallery.querySelector('[data-count]');
    const smooth = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    thumbs.forEach((thumb, i) =>
      thumb.addEventListener('click', () => track.scrollTo({ left: slides[i].offsetLeft, behavior: smooth })),
    );

    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = slides.indexOf(entry.target);
          thumbs.forEach((t, j) => t.setAttribute('aria-current', String(j === index)));
          if (count) count.textContent = `${index + 1} / ${slides.length}`;
        }),
      { root: track, threshold: 0.6 },
    );
    slides.forEach((slide) => observer.observe(slide));
  });

  /* ---------------- Variant picker (multi-variant products) ---------------- */
  const variantsJson = document.querySelector('[data-axus-variants]');
  if (variantsJson) {
    const variants = JSON.parse(variantsJson.textContent);
    const form = document.querySelector('.axus-product-form');
    const pct = Number(form && form.dataset.axusPct) || 0;
    const groups = [...document.querySelectorAll('[data-axus-option]')];
    const priceEl = document.querySelector('[data-axus-price]');
    const stockEl = document.querySelector('[data-axus-stock]');
    const update = () => {
      const chosen = groups.map((g) => {
        const on = g.querySelector('[aria-pressed="true"]');
        return on ? on.dataset.value : null;
      });
      const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
      const input = form && form.querySelector('[name="id"]');
      const button = form && form.querySelector('[name="add"]');
      if (!v || !input || !button) {
        if (button) button.disabled = true;
        return;
      }
      input.value = v.id;
      priceEl.innerHTML = pct
        ? `<span class="axus-p axus-p--lg"><span class="axus-p__now">${esc(v.final)}</span> <s class="axus-p__was">${esc(v.price)}</s> <span class="axus-p__off">${pct}% off</span></span>`
        : `<span class="axus-p axus-p--lg"><span class="axus-p__now">${esc(v.price)}</span></span>`;
      stockEl.textContent = v.available ? 'In stock' : 'Out of stock';
      stockEl.classList.toggle('axus-stock--out', !v.available);
      button.disabled = !v.available;
      button.querySelector('span').textContent = v.available ? 'Add to cart' : 'Sold out';
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    };
    groups.forEach((g) =>
      g.addEventListener('click', (event) => {
        const button = event.target.closest('button[data-value]');
        if (!button) return;
        g.querySelectorAll('button[data-value]').forEach((x) => x.setAttribute('aria-pressed', String(x === button)));
        const label = g.querySelector('[data-axus-option-value]');
        if (label) label.textContent = button.dataset.value;
        update();
      }),
    );
  }

  /* ---------------- Quantity ---------------- */
  document.querySelectorAll('[data-axus-qty]').forEach((qty) => {
    const input = qty.querySelector('input');
    qty.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-step]');
      if (!button) return;
      const next = Math.min(99, Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.step)));
      input.value = String(next);
    });
  });

  /* ---------------- "Fits your vehicle" on product pages ---------------- */
  const fitcheck = document.querySelector('[data-axus-fitcheck]');
  const runFitcheck = (vehicle) => {
    if (!fitcheck || !vehicle) return;
    loadData(fitcheck.dataset.src)
      .then((data) => {
        const handle = fitcheck.dataset.handle;
        const row = data.fits.find((f) => f[6] === handle || f[7] === handle);
        if (!row) return;
        const name = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
        const fits =
          data.makes[row[0]] === vehicle.make &&
          row[1] === vehicle.model &&
          row[3] <= vehicle.year &&
          vehicle.year <= row[4];
        fitcheck.className = `axus-fitcheck ${fits ? 'axus-fitcheck--yes' : 'axus-fitcheck--no'}`;
        fitcheck.innerHTML = fits
          ? `✓ Listed for your ${esc(name)}. Check your rail type below before ordering.`
          : `This set isn't listed for your saved ${esc(name)}. <a href="${root}#find-your-fit">Find the right fit</a>`;
        fitcheck.hidden = false;
      })
      .catch(() => {});
  };
  runFitcheck(readVehicle());
  document.addEventListener('axus:vehicle', (event) => runFitcheck(event.detail));

  /* ---------------- Announcement rotator ---------------- */
  document.querySelectorAll('[data-axus-rotator]').forEach((bar) => {
    const slides = [...bar.querySelectorAll('.axus-announce__slide')];
    if (slides.length < 2 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const interval = Number(bar.dataset.interval) || 5000;
    let index = 0;
    let paused = false;
    const show = (next) => {
      const current = slides[index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      current.querySelectorAll('a').forEach((a) => (a.tabIndex = -1));
      setTimeout(() => current.classList.remove('is-leaving'), 600);
      index = next;
      slides[index].classList.add('is-active');
      slides[index].removeAttribute('aria-hidden');
      slides[index].querySelectorAll('a').forEach((a) => a.removeAttribute('tabindex'));
    };
    ['mouseenter', 'focusin'].forEach((e) => bar.addEventListener(e, () => (paused = true)));
    ['mouseleave', 'focusout'].forEach((e) => bar.addEventListener(e, () => (paused = false)));
    setInterval(() => {
      if (!paused && !document.hidden) show((index + 1) % slides.length);
    }, interval);
  });

  /* ---------------- Estimated delivery dates ---------------- */
  document.querySelectorAll('[data-axus-eta]').forEach((eta) => {
    const target = eta.querySelector('[data-dates]');
    const addBusinessDays = (date, days) => {
      const d = new Date(date);
      let left = days;
      while (left > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0 && d.getDay() !== 6) left -= 1;
      }
      return d;
    };
    const fmt = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const today = new Date();
    const from = addBusinessDays(today, Number(eta.dataset.min) || 1);
    const to = addBusinessDays(today, Number(eta.dataset.max) || 3);
    if (target) target.textContent = `(${fmt(from)} – ${fmt(to)})`;
  });

  /* ---------------- Makes toggle (mobile) ---------------- */
  document.querySelectorAll('[data-axus-more]').forEach((button) =>
    button.addEventListener('click', () => {
      const list = document.getElementById(button.getAttribute('aria-controls'));
      const open = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', String(open));
      list.classList.toggle('is-open', open);
      button.textContent = open ? button.dataset.less : button.dataset.more;
    }),
  );
})();
